import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

export interface MonthYear {
  month: number;
  year: number;
}

@Component({
  selector: 'app-custom-month-year-picker',
  templateUrl: './custom-month-year-picker.component.html',
  styleUrls: ['./custom-month-year-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomMonthYearPickerComponent),
      multi: true
    }
  ]
})
export class CustomMonthYearPickerComponent implements OnInit, ControlValueAccessor {
  @Input() placeholder: string = 'Select Month & Year';
  @Input() disabled: boolean = false;
  @Input() minDate?: Date;
  @Input() maxDate?: Date;

  @Output() monthYearChange = new EventEmitter<MonthYear | null>();

  isOpen = false;
  selectedMonthYear: MonthYear | null = null;
  currentMonth: Date = new Date();
  months = [
    'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
    'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
  ];

  isYearView = false;
  yearRange: number[] = [];
  currentYearRange = 0;

  private onChange = (value: any) => {};
  private onTouched = () => {};

  ngOnInit() {
    this.generateYearRange();
  }

  writeValue(value: MonthYear | null): void {
    this.selectedMonthYear = value;
    if (value) {
      this.currentMonth = new Date(value.year, value.month, 1);
      this.generateYearRange();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggleCalendar(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    this.closeAllViews();

    if (this.isOpen) {
      if (this.selectedMonthYear) {
        this.currentMonth = new Date(this.selectedMonthYear.year, this.selectedMonthYear.month, 1);
      }
      this.generateYearRange();
    }

    this.onTouched();
  }

  closeCalendar(): void {
    this.isOpen = false;
    this.closeAllViews();
  }

  selectMonth(monthIndex: number): void {
    this.selectedMonthYear = {
      month: monthIndex,
      year: this.currentMonth.getFullYear()
    };
    this.onChange(this.selectedMonthYear);
    this.monthYearChange.emit(this.selectedMonthYear);
    this.closeCalendar();
  }

  selectYear(year: number): void {
    this.currentMonth = new Date(year, this.currentMonth.getMonth(), 1);
    this.generateYearRange();
    this.isYearView = false;
  }

  clearSelection(): void {
    this.selectedMonthYear = null;
    this.onChange(this.selectedMonthYear);
    this.monthYearChange.emit(this.selectedMonthYear);
  }

  previousYearRange(): void {
    this.currentYearRange -= 12;
    this.generateYearRange();
  }

  nextYearRange(): void {
    this.currentYearRange += 12;
    this.generateYearRange();
  }

  generateYearRange(): void {
    const currentYear = this.currentMonth.getFullYear();
    const startYear = Math.floor(currentYear / 12) * 12 + this.currentYearRange;
    this.yearRange = Array.from({ length: 12 }, (_, i) => startYear + i);
  }

  goToYearView(): void {
    this.isYearView = true;
    this.generateYearRange();
  }

  goToMonthView(): void {
    this.isYearView = false;
  }

  closeAllViews(): void {
    this.isYearView = false;
  }

  isSelected(monthIndex: number): boolean {
    return this.selectedMonthYear?.month === monthIndex && 
           this.selectedMonthYear?.year === this.currentMonth.getFullYear();
  }

  isDisabled(monthIndex: number, year: number): boolean {
    const date = new Date(year, monthIndex, 1);
    if (this.minDate && date < this.minDate) return true;
    if (this.maxDate && date > this.maxDate) return true;
    return false;
  }

  getDisplayValue(): string {
    if (!this.selectedMonthYear) return '';
    
    const monthName = this.months[this.selectedMonthYear.month];
    return `${monthName} ${this.selectedMonthYear.year}`;
  }

  getMonthYearDisplay(): string {
    return `${this.months[this.currentMonth.getMonth()]} ${this.currentMonth.getFullYear()}`;
  }
} 